import React from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Link } from 'react-router-dom'
import { FiPhone, FiMail, FiMapPin, FiClock, FiArrowRight } from 'react-icons/fi'

const PageContainer = styled.div`
  padding-top: 80px;
`

const HeroSection = styled.section`
  background: var(--gradient-secondary);
  color: white;
  padding: 6rem 0 4rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -30%;
    left: -10%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 100px;
    background: var(--color-bg-card);
    clip-path: polygon(0 50%, 100% 0, 100% 100%, 0 100%);
  }
`

const AboutSection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(180deg, var(--color-bg-card) 0%, var(--color-bg-subtle) 100%);
`

const AboutContent = styled.div`
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: 0 var(--container-padding);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`

const AboutText = styled.div`
  h2 {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    color: var(--color-text);
  }

  p {
    font-size: 1.125rem;
    margin-bottom: 1.5rem;
    color: var(--color-text-light);
    line-height: 1.7;
  }
`

const AboutImage = styled.div`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -15px;
    right: -15px;
    bottom: 15px;
    left: 15px;
    background: var(--gradient-accent);
    border-radius: var(--border-radius-xl);
    z-index: -1;
    opacity: 0.4;
  }

  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: var(--border-radius-xl);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.02);
  }
`

const ValuesSection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, var(--color-bg-subtle) 0%, var(--color-bg-alt) 100%);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    bottom: 20%;
    left: -15%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(37, 99, 235, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    z-index: 0;
  }
`

const ValuesGrid = styled.div`
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: 0 var(--container-padding);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`

const ValueCard = styled(motion.div)`
  background: var(--color-bg-card);
  padding: 2.5rem;
  border-radius: var(--border-radius-xl);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid rgba(37, 99, 235, 0.1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-accent);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(37, 99, 235, 0.15);
    border-color: rgba(37, 99, 235, 0.2);

    &::before {
      transform: scaleX(1);
    }
  }

  h3 {
    color: var(--color-primary);
    margin-bottom: 1rem;
    font-size: 1.5rem;
  }

  p {
    color: var(--color-text-light);
    line-height: 1.6;
  }
`

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    
    .btn {
      width: 100%;
      max-width: 280px;
    }
  }
`

const CTASection = styled.section`
  background: var(--gradient-dark);
  padding: 5rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 50%);
    animation: pulse 4s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.1); opacity: 0.3; }
  }
`

const AboutPage: React.FC = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 })
  const [aboutRef, aboutInView] = useInView({ triggerOnce: true, threshold: 0.1 })
  const [valuesRef, valuesInView] = useInView({ triggerOnce: true, threshold: 0.1 })

  const values = [
    {
      title: 'Profesjonalizm',
      description: 'Każde zlecenie traktujemy z najwyższą starannością. Nasz zespół to doświadczeni specjaliści z wieloletnim stażem.'
    },
    {
      title: 'Niezawodność',
      description: 'Dotrzymujemy terminów i obietnic. Możesz na nas liczyć w każdej sytuacji, także w nagłych przypadkach.'
    },
    {
      title: 'Jakość',
      description: 'Używamy tylko sprawdzonych materiałów i nowoczesnych technologii. Gwarantujemy trwałość naszych rozwiązań.'
    }
  ]

  return (
    <PageContainer>
      <HeroSection ref={heroRef}>
        <div className="container">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            O nas
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{ fontSize: '1.25rem', opacity: 0.9 }}
          >
            Poznaj zespół FachFIX i naszą historię
          </motion.p>
        </div>
      </HeroSection>

      <AboutSection ref={aboutRef}>
        <AboutContent>
          <AboutText>
            <motion.h2
              initial={{ opacity: 0, x: -30 }}
              animate={aboutInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              Kim jesteśmy?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, x: -30 }}
              animate={aboutInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              FachFIX to dynamicznie rozwijająca się firma serwisowa, specjalizująca się 
              w obsłudze technicznej obiektów komercyjnych i przemysłowych w Trójmieście.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, x: -30 }}
              animate={aboutInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Nasza misja to zapewnienie niezawodnego wsparcia technicznego dla firm, 
              które potrzebują profesjonalnych usług serwisowych. Rozumiemy specyfikę 
              pracy w środowisku biznesowym i dostosowujemy nasze działania do Twoich potrzeb.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, x: -30 }}
              animate={aboutInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Oferujemy kompleksowe rozwiązania w zakresie instalacji hydraulicznych, 
              grzewczych, ślusarki oraz zaplanowanych prac serwisowych. Nasz zespół to doświadczeni 
              specjaliści, którzy gwarantują najwyższą jakość wykonywanych usług.
            </motion.p>
          </AboutText>
          <AboutImage>
            <motion.img
              src="/images/bb2e8a7e-10e4-4deb-9659-b10fdb198b36.png"
              alt="Zespół FachFIX"
              initial={{ opacity: 0, x: 30 }}
              animate={aboutInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
            />
          </AboutImage>
        </AboutContent>
      </AboutSection>

      <ValuesSection ref={valuesRef}>
        <div className="container">
          <motion.h2
            className="text-center"
            style={{ marginBottom: '3rem', fontSize: '2.5rem' }}
            initial={{ opacity: 0, y: 30 }}
            animate={valuesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            Nasze wartości
          </motion.h2>
          <ValuesGrid>
            {values.map((value, index) => (
              <ValueCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={valuesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
              >
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </ValueCard>
            ))}
          </ValuesGrid>
        </div>
      </ValuesSection>

      <CTASection>
        <div className="container">
          <h2 style={{ marginBottom: '1.5rem', color: 'white' }}>
            Gotowy na współpracę?
          </h2>
          <p style={{ fontSize: '1.125rem', marginBottom: '2rem', opacity: 0.9, color: 'white' }}>
            Skontaktuj się z nami i przekonaj się o naszym profesjonalizmie
          </p>
          <CTAButtons>
            <Link to="/kontakt" className="btn btn-white btn-lg">
              Skontaktuj się z nami
              <FiArrowRight style={{ marginLeft: '0.5rem' }} />
            </Link>
            <a href="tel:510184702" className="btn btn-secondary btn-lg">
              <FiPhone />
              Zadzwoń teraz
            </a>
          </CTAButtons>
        </div>
      </CTASection>
    </PageContainer>
  )
}

export default AboutPage 